import { dataServiceApi } from "contexts/axiosApiInstance";
import { createLogger } from "debug/DebugingLogger";
const log = createLogger("Tokens API");

export type TokenScope = "read" | "write";

export interface TokenResponse {
  token_id: string;
  status: string;
  expires_at: number;
  scope: string;
  description: string;
  created_at: number;
  /** Org the token is bound to; null for tokens predating org-gating. */
  org_id?: string | null;
}

export const fetchTokens = async (): Promise<TokenResponse[]> => {
  try {
    const response = await dataServiceApi.post("/tokens/list");
    if (response.data?.status === "success") {
      log.info("Tokens fetched successfully", response.data.message.length);
      return response.data.message;
    }
  } catch (error) {
    log.error("Failed to fetch tokens", error);
  }
  throw new Error("Failed to fetch tokens");
};

export const generateToken = async (
  expirationTime: number,
  description: string,
  scope: TokenScope,
  orgId: string,
): Promise<{ token: string; created_at: number }> => {
  try {
    const response = await dataServiceApi.post("/tokens/generate", {
      expiration_time: expirationTime,
      scope,
      description,
      // Tokens are always org-bound (S8): the active org is the ceiling.
      org_id: orgId,
    });

    if (response.data?.status === "success") {
      log.info("Token generated successfully");
      return {
        token: response.data.token,
        created_at: response.data.created_at,
      };
    }
  } catch (error) {
    log.error("Failed to generate token", error);
  }
  throw new Error("Failed to generate token");
};

export const revokeToken = async (tokenId: string): Promise<void> => {
  try {
    const response = await dataServiceApi.post("/tokens/revoke", tokenId);
    if (response.data?.status === "success") {
      log.info("Token revoked successfully");
      return;
    }
  } catch (error) {
    log.error("Failed to revoke token", error);
  }
  throw new Error("Failed to revoke token");
};

export const deleteToken = async (tokenId: string): Promise<void> => {
  try {
    // axios sends a bare string as text/plain; the door wants a JSON string.
    const response = await dataServiceApi.delete("/tokens/delete", {
      data: JSON.stringify(tokenId),
      headers: { "Content-Type": "application/json" },
    });
    if (response.data?.status === "success") {
      log.info("Token deleted successfully");
      return;
    }
  } catch (error) {
    log.error("Failed to delete token", error);
  }
  throw new Error("Failed to delete token");
};
